/* Copyright (c) 2020-present CloudIO Inc */

export const managerJs = `/* Copyright (c) 2020-present CloudIO Inc */

import { baseComponentManager, defaultCommonProps, ComponentManagerFn } from 'cloudio';

/*
Perform the following steps in Cloud Function with Module URI custom-component-types under cloudio app. If the module is not present, create it by using the script provided @ https://next-docs.cloudio.io/ui/custom-component/types

// ChangeThisToYourComponentName
// 1. Define the type for the props to your custom component extending CommonProps
export interface ChangeThisToYourComponentName extends CommonProps {
  text?: ExpressionValue;
  onClickFunction?: string;
  // add more props here
}
// 2. Define the item type of your custom component extending BaseComponentItem
export interface ChangeThisToYourComponentNameItem extends BaseComponentItem {
  props: ChangeThisToYourComponentName;
  type: 'ChangeThisToYourComponentName';
}
// 3. Register your custom component type by adding an entry to CustomComponentItemTypeRegistry
export interface CustomComponentItemTypeRegistry {
  ...
  ChangeThisToYourComponentName: ChangeThisToYourComponentName;
  ...
}
// 4. Added your custom component item type to CustomComponentItem
export type CustomComponentItem = ... | ChangeThisToYourComponentNameItem | ...;
*/

const ChangeThisToYourComponentNameManager: ComponentManagerFn<'ChangeThisToYourComponentName'> = (
  platform, type
) => {
  return {
    ...baseComponentManager(platform, type),
    type,
    name: "My Custom Component",
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick', 'PulseTip']).includes(childType),
    defaultChildren: ['TriggerOnClick'],
    defaultValue: {
      ...defaultCommonProps,
      text: { value: 'Change Me!', type: 'text' },
    },
    props: { // refer https://next-docs.cloudio.io/ui/custom-component/sample-property-definitions for more examples
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
      },
      onClickFunction: {
        label: 'On Click',
        type: 'controllerFunction',
        category: 'Controllers',
      },
    },
    generateShortId: (item) =>
      item.text?.type === 'text' ? item.text.value : '',
    category: 'Custom',
    icon: { iconName: 'check' },
    getParentItemTypes: () => ['Box', 'FlexItem', 'ColumnPanel'],
    description:
      'Description about my custom component... CHANGE ME!',
  };
};

export default ChangeThisToYourComponentNameManager;
`;

export const componentJs = `/* Copyright (c) 2020-present CloudIO Inc */

import React from 'react';
import { useStringExpression, useTrigger, useSetCurrentRecord, useAsyncControllerFunction, useRowIndex, BaseProps, ExpressionValue } from 'cloudio';
import { ChangeThisToYourComponentName } from '@cloudio-saas/custom-types';

interface Props extends BaseProps<any> {
  text: ExpressionValue,
  onClickFunction: string,
}

interface Props extends Required<ChangeThisToYourComponentName>, BaseProps<'ChangeThisToYourComponentName'> {   }

function ChangeThisToYourComponentNameComponent({ domProps, className, children, pageId, appUid, roleUid, itemId, text, platform, onClickFunction }: Props, ref: React.Ref<HTMLDivElement>) {
  const rowIndex = useRowIndex();
  const textResult = useStringExpression({
    exp: text,
    context: { appUid, pageId, itemId, property: 'text', data: { rowIndex } },
  });
  const [onClickTrigger, clickInProgress] = useTrigger(
    {
      platform,
      appUid,
      roleUid,
      pageId,
      itemId,
    },
    'TriggerOnClick',
  );
  const setCurrentRecord = useSetCurrentRecord();
  const onClickCallback = useAsyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: onClickFunction,
  });
  const onClick = React.useCallback(
    async (e) => {
      if (rowIndex !== undefined) {
        setCurrentRecord(rowIndex);
      }
      if (platform.isRecording()) {
        platform.recordLine(itemId, \`await ui.click({ testid: '\${itemId}-some-unique-testid' });\`);
      }
      await onClickCallback({ event: e, eventName: 'onClick' });
      if (onClickTrigger) {
        await onClickTrigger(e);
      }
    },
    [onClickCallback, onClickTrigger, rowIndex, setCurrentRecord],
  );

  return (
    <div
      ref={ref}
      data-testid={\`\${itemId}-some-unique-testid\`}
      className={className}
      onClick={onClick}
      {...domProps}
    >
      {textResult}
      {children}
    </div>
  );
}

export default React.forwardRef(ChangeThisToYourComponentNameComponent);`;
